import { ChangeDetectionStrategy, Component, Input } from '@angular/core';
import { TranslationService } from '@ng-omar/translation';
import { GanttDateService, MonthHeader } from '../services';

@Component({
    selector: 'app-gantt-header',
    // cspell:disable
    template: `
        <div
            class="flex w-full border-b border-slate-100 bg-slate-50"
            role="row"
            aria-rowindex="1"
        >
            <div
                class="flex w-1/3 min-w-[300px] flex-col items-center justify-center border-e border-slate-200 p-4"
                role="columnheader"
                [attr.aria-label]="getTimelineAriaLabel()"
            >
                @if (title) {
                    <div class="flex items-center justify-center gap-3">
                        <h2
                            class="text-lg font-bold text-slate-800"
                            [id]="'gantt-title-' + titleId"
                        >
                            {{ title }}
                        </h2>
                        @if (progress !== undefined && progress !== null) {
                            <span
                                class="inline-flex items-center rounded-full bg-primary-100 px-2.5 py-1 text-xs font-medium text-primary-700"
                                [attr.aria-label]="getProgressAriaLabel()"
                            >
                                {{ getProgressDisplay() }}%
                            </span>
                        }
                    </div>
                }
                <p
                    class="text-sm text-slate-500"
                    [attr.dir]="
                        translationService.currentLanguage?.isRtl
                            ? 'rtl'
                            : 'ltr'
                    "
                    [attr.aria-describedby]="'gantt-title-' + titleId"
                >
                    {{ getDateRangeDisplay() }}
                </p>
            </div>

            <div class="flex flex-1" role="row">
                @for (
                    month of displayMonths;
                    track trackByMonth(i, month);
                    let i = $index
                ) {
                    <div
                        class="flex-1 border-e border-slate-100 p-3 text-center text-sm font-medium text-slate-600 md:text-base"
                        role="columnheader"
                        [attr.aria-label]="
                            'Month: ' + month.month + ' ' + month.year
                        "
                        [attr.aria-colindex]="i + 2"
                    >
                        <div class="flex flex-col items-center leading-tight">
                            <span class="text-xs text-slate-500 md:text-sm">{{
                                month.month
                            }}</span>
                            <span class="text-xs text-slate-600 md:text-sm">{{
                                month.year
                            }}</span>
                        </div>
                    </div>
                }
            </div>
        </div>
    `,
    // cspell:enable
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: false,
})
export class GanttHeaderComponent {
    @Input() public title?: string;
    @Input() public progress?: number;
    @Input() public startDate: Date | null = null;
    @Input() public endDate: Date | null = null;
    @Input() public displayMonths: MonthHeader[] = [];
    @Input() public titleId: string = Math.random().toString(36).slice(2, 11);

    public constructor(
        public readonly translationService: TranslationService,
        private readonly dateService: GanttDateService
    ) {}

    public trackByMonth(index: number, month: MonthHeader): string {
        return `${month.key}-${index}`;
    }

    public getTimelineAriaLabel(): string {
        if (!this.startDate || !this.endDate) {
            return 'Project timeline';
        }
        return `Project timeline from ${this.formatDate(
            this.startDate
        )} to ${this.formatDate(this.endDate)}`;
    }

    public getProgressAriaLabel(): string {
        if (this.progress === undefined || this.progress === null) return '';
        return `Overall progress: ${Math.round(this.progress * 100)} percent`;
    }

    public getProgressDisplay(): string {
        if (this.progress === undefined || this.progress === null) return '0';
        return Math.round(this.progress * 100).toString();
    }

    public getDateRangeDisplay(): string {
        if (!this.startDate || !this.endDate) return '';
        const fromDate = this.formatDate(this.startDate);
        const toDate = this.formatDate(this.endDate);
        return `${fromDate} - ${toDate}`;
    }

    private formatDate(date: Date): string {
        return this.dateService.formatDate(date);
    }
}
